/** @format */

import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import {
  GTGT_FILTER_NAME,
  getGtgtDisplayValue,
  hasGtgtSelectableOptions,
} from '@plp/data-access/constants';
import { selectSelectedFilters, selectSkeletonLoader } from '@plp/data-access/state';

import { Chain } from '@product-recommendations/data-access';
import { FacetVO } from '@plp/data-access/interfaces';
import { FilterIdPipe } from '../pipes/filter-id.pipe';
import { HmfGlobalChipsComponent } from '@dsg-tech/homefield-angular';
import { ProductListFilterDrawerService } from '@plp/data-access/services';
import { Store } from '@ngrx/store';

@Component({
  selector: 'plp-components-exposed-filters',
  imports: [HmfGlobalChipsComponent, FilterIdPipe],
  templateUrl: './exposed-filters.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExposedFiltersComponent {
  private readonly store = inject(Store);
  private readonly filterDrawerService = inject(ProductListFilterDrawerService);
  private readonly chain = inject(Chain);

  readonly anchorChipRef = viewChild.required<ElementRef<HTMLElement>>('anchorChip');

  filter = input.required<FacetVO>();
  isMobile = input.required<boolean>();
  readonly isOpen = input(false);

  readonly desktopPopoverRequested = output<{
    anchorElement: HTMLElement;
    filter: FacetVO;
  }>();

  readonly desktopPopoverTabRequested = output<void>();
  readonly desktopPopoverShiftTabRequested = output<void>();

  private readonly selectedFilters = this.store.selectSignal(selectSelectedFilters);

  readonly selectedOptions = computed(
    () => this.selectedFilters()[this.filter().attrIdentifier] || [],
  );

  readonly showSkeletonLoader = this.store.selectSignal(selectSkeletonLoader);

  readonly isGtgtFilter = computed(() => this.filter().attrName === GTGT_FILTER_NAME);

  readonly shouldHideGtgtChip = computed(
    () => this.isGtgtFilter() && !hasGtgtSelectableOptions(this.filter().valueList),
  );

  readonly chipLabel = computed(() => {
    const filter = this.filter();
    const selectedCount = this.selectedOptions().length;
    const selectedFilterCount = selectedCount > 0 ? `\u00A0\u00A0(${selectedCount})` : '';

    if (this.isGtgtFilter()) {
      return getGtgtDisplayValue(this.chain.chainIdentifierAbbr) + selectedFilterCount;
    }
    return filter?.attrName + selectedFilterCount;
  });

  readonly refinementSource = 'DynamicExposed' as const;

  exposedFilterAction() {
    if (this.isMobile()) {
      const scrollToFilter = this.filter() ?? null;
      this.filterDrawerService.open(scrollToFilter, this.refinementSource);
      return;
    }

    const anchorElement = this.anchorChipRef().nativeElement;
    this.desktopPopoverRequested.emit({
      anchorElement,
      filter: this.filter(),
    });
  }

  handleDesktopPopoverTab(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;

    if (this.isMobile() || !this.isOpen() || keyboardEvent.shiftKey) {
      return;
    }

    keyboardEvent.preventDefault();
    this.desktopPopoverTabRequested.emit();
  }

  handleDesktopPopoverShiftTab(): void {
    if (this.isMobile() || !this.isOpen()) {
      return;
    }

    this.desktopPopoverShiftTabRequested.emit();
  }
}
